import { useState, useEffect, useRef } from "react";
import Sidebar from "../common/Sidebar";
import Logout from "../common/Logout";
import "../customCss/AllUsers.css";
import { TextField, Autocomplete } from "@mui/material";
import { FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import moment from "moment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import LoadingBar from "react-top-loading-bar";
import DataService from "../services/data.service";

const Invoices = () => {
  const ref = useRef(null);
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [newPopup, setNewPopup] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [filterInvoices, setFilterInvoices] = useState([]);

  // form states
  const [user, setUser] = useState(null);
  const [program, setProgram] = useState(null);
  const [dayType, setDayType] = useState("Full Day");
  const [amount, setAmount] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [goldenText, setGoldenText] = useState("");
  const [goldenPrice, setGoldenPrice] = useState("");
  const [enrollmentText, setEnrollmentText] = useState("");
  const [enrollmentPrice, setEnrollmentPrice] = useState("");
  const [lunchText, setLunchText] = useState("");
  const [lunchPrice, setLunchPrice] = useState("");

  // data lists
  const [usersList, setUsersList] = useState([]);
  const [programsList, setProgramsList] = useState([]);
  // delete modal
  const [deletePopup, setDeletePopup] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // search filters
  const [searchName, setSearchName] = useState("");
  const [searchDate, setSearchDate] = useState("");

  useEffect(() => {
    getAllInvoices();
    fetchLists();
  }, []);

  const fetchLists = async () => {
    try {
      const usersRes = await DataService.getAllUsers();
      const programsRes = await DataService.getAllProgramPrices();
      setUsersList(usersRes?.data?.data || []);
      setProgramsList(programsRes?.data?.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load lists");
    }
  };

  const getAllInvoices = async () => {
    setLoading(true);
    ref.current?.continuousStart();
    try {
      const res = await DataService.getAllInvoices();
      const data = res?.data?.data || [];
      setInvoices(data?.reverse() || []);
      setFilterInvoices(data?.reverse() || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch invoices");
    } finally {
      setLoading(false);
      ref.current?.complete();
    }
  };

  const resetFields = () => {
    setUser(null);
    setProgram(null);
    setAmount("");
    setStartDate(null);
    setGoldenText("");
    setGoldenPrice("");
    setEnrollmentText("");
    setEnrollmentPrice("");
    setLunchText("");
    setLunchPrice("");
  };

  const calculatePrice = (prog, type, date) => {
    if (!prog) return "";
    let basePrice = 0;
    if (type === "Half Day") {
      basePrice = prog.halfDayPrice || 0;
    } else {
      basePrice = prog.fullDayPrice || 0;
    }

    if (!date) return basePrice;

    const d = dayjs(date);
    const dayOfMonth = d.date();
    
    // If start date is 1st, full price
    if (dayOfMonth === 1) {
      return basePrice;
    }

    // Pro-rated calculation
    const daysInMonth = d.daysInMonth();
    const remainingDays = daysInMonth - dayOfMonth + 1;
    const dailyRate = basePrice / daysInMonth;
    const calculated = dailyRate * remainingDays;
    
    return parseFloat(calculated.toFixed(2));
  };

  const handleProgramSelect = (e, newProgram) => {
    setProgram(newProgram || null);
    const newPrice = calculatePrice(newProgram, dayType, startDate);
    setAmount(newPrice);
  };

  const handleDayTypeChange = (e, newType) => {
    setDayType(newType);
    const newPrice = calculatePrice(program, newType, startDate);
    setAmount(newPrice);
  };

  const handleDateChange = (newDate) => {
    setStartDate(newDate);
    const newPrice = calculatePrice(program, dayType, newDate);
    setAmount(newPrice);
  };

  const handleCreate = async () => {
    if (!user || !program || !amount || !startDate) {
      toast.error("All required fields must be filled");
      return;
    }
    if (goldenText && (!goldenPrice || Number(goldenPrice) <= 0)) {
      toast.error("Please enter a valid Golden Hour cost");
      return;
    }
    if (enrollmentText && (!enrollmentPrice || Number(enrollmentPrice) <= 0)) {
      toast.error("Please enter a valid Enrollment cost");
      return;
    }
    if (lunchText && (!lunchPrice || Number(lunchPrice) <= 0)) {
      toast.error("Please enter a valid Lunch cost");
      return;
    }

    const payload = {
      userId: user._id,
      program: program.name,
      dayType,
      amount: Number(amount),
      startDate: dayjs(startDate).format("YYYY-MM-DD"),
      goldenText,
      goldenPrice: goldenPrice ? Number(goldenPrice) : 0,
      enrollmentText,
      enrollmentPrice: enrollmentPrice ? Number(enrollmentPrice) : 0,
      lunchText,
      lunchPrice: lunchPrice ? Number(lunchPrice) : 0,
    };

    setButtonLoading(true);
    try {
      await DataService.createInvoice(payload);
      toast.success("Invoice created successfully");
      setNewPopup(false);
      resetFields();
      getAllInvoices();
    } catch (error) {
      console.error(error);
      toast.error("Failed to create invoice");
    } finally {
      setButtonLoading(false);
    }
  };

  const confirmDelete = (item) => {
    setSelectedInvoice(item);
    setDeletePopup(true);
  };

  const handleDelete = async () => {
    if (!selectedInvoice) return;
    setDeleteLoading(true);
    try {
      await DataService.deleteInvoice(selectedInvoice._id);
      toast.success("Invoice deleted successfully");
      getAllInvoices();
      setDeletePopup(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete invoice");
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    const filterUsers = () => {
      if (!invoices || invoices.length === 0) return;
      if (!searchName && !searchDate) {
        setFilterInvoices(invoices);
        return;
      }
      const filteredData = invoices.filter((b) => {
        const nameMatch = searchName
          ? b?.userId?.parentName
              ?.toLowerCase()
              .includes(searchName.toLowerCase())
          : true;
        const dateMatch = searchDate
          ? moment(b?.createdAt).format("YYYY-MM-DD") === searchDate
          : true;
        return nameMatch && dateMatch;
      });
      setFilterInvoices(filteredData);
    };
    filterUsers();
  }, [searchName, searchDate, invoices]);

  const userNameFilder = (id) => {
    return usersList?.find((uIn) => uIn?._id === id)?.parentName;
  };

  return (
    <div className="main_section">
      <div className="main_flex">
        <div className="main_flexL">
          <Sidebar />
        </div>

        <div className="main_flexR">
          <div className="right_title">
            <h1>Invoices</h1>
            <Logout />
          </div>

          <LoadingBar color="#84C342" ref={ref} height={5} />

          <div className="filters_sec">
            <div className="filtersL">
              <div className="row">
                {/* <div className="col-sm-3">
                  <div className="form-floating mb-3">
                    <input
                      type="text"
                      placeholder="Search by Parent Name"
                      value={searchName}
                      className="form-control"
                      id="floatingInputParent"
                      onChange={(e) => setSearchName(e.target.value)}
                    />
                    <label htmlFor="floatingInputParent">
                      Search by Parent Name
                    </label>
                  </div>
                </div> */}
                <div className="col-sm-3">
                  <div className="form-floating mb-3">
                    <input
                      type="date"
                      value={searchDate}
                      placeholder="Created at Date"
                      className="form-control"
                      id="floatingInputDate"
                      onChange={(e) => setSearchDate(e.target.value)}
                    />
                    <label htmlFor="floatingInputDate">Created at Date</label>
                  </div>
                </div>
              </div>
            </div>

            <div className="filtersR text-end">
              <button
                id="reloadBtn"
                onClick={getAllInvoices}
                disabled={loading}
              >
                <i className={`fa fa-sync-alt ${loading ? "fa-spin" : ""}`}></i>{" "}
                Reload
              </button>

              <button
                className="btn btn-primary ms-3"
                onClick={() => {
                  resetFields();
                  setNewPopup(true);
                }}
              >
                <i className="fas fa-plus" /> Create Invoice
              </button>
            </div>
          </div>

          <div className="main_innerSec">
            <div className="main_booksSec">
              <table className="table table-striped table-bordered">
                <thead>
                  <tr style={{ background: "#f5f5f5" }}>
                    <th>S. No.</th>
                    <th>Parent Name</th>
                    <th>Program</th>
                    <th>Day Type</th>
                    <th>Amount ($)</th>
                    <th>Start Date</th>
                    <th>Golden Hour</th>
                    <th>Enrollment</th>
                    <th>Lunch</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filterInvoices?.length > 0 ? (
                    filterInvoices.map((item, idx) => (
                      <tr key={item._id}>
                        <td>{idx + 1}</td>
                        <td>{userNameFilder(item?.userId) || "-"}</td>
                        <td>{item.program || "-"}</td>
                        <td>{item.dayType || "-"}</td>
                        <td>${item.amount?.toFixed(2)}</td>
                        <td>
                          {item.startDate
                            ? moment(item.startDate).format("DD/MM/YYYY")
                            : "-"}
                        </td>

                        {/* Golden Hour */}
                        <td>
                          {item.goldenText ? (
                            <>
                              <strong>{item.goldenText}</strong>
                              <br />
                              <span>${item.goldenPrice?.toFixed(2)}</span>
                            </>
                          ) : (
                            "-"
                          )}
                        </td>

                        {/* Enrollment */}
                        <td>
                          {item.enrollmentText ? (
                            <>
                              <strong>{item.enrollmentText}</strong>
                              <br />
                              <span>${item.enrollmentPrice?.toFixed(2)}</span>
                            </>
                          ) : (
                            "-"
                          )}
                        </td>

                        {/* Lunch */}
                        <td>
                          {item.lunchText ? (
                            <>
                              <strong>{item.lunchText}</strong>
                              <br />
                              <span>${item.lunchPrice?.toFixed(2)}</span>
                            </>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td>
                          {moment(item.createdAt).format("DD/MM/YYYY hh:mm A")}
                        </td>

                        <td className="text-center">
                          <FaTrash
                            className="text-danger"
                            style={{ cursor: "pointer" }}
                            onClick={() => confirmDelete(item)}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="text-center">
                        No invoices found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Create Invoice Popup */}
      {newPopup && (
        <div className="popOuter">
          <div className="popInner" style={{ maxWidth: 700, minWidth: 600 }}>
            <i
              className="fas fa-times"
              onClick={() => {
                setNewPopup(false);
                resetFields();
              }}
            />
            <div className="popBody">
              <div className="popForm">
                <h2 className="mb-3">Create Invoice</h2>

                <div className="mb-3">
                  <Autocomplete
                    options={usersList}
                    getOptionLabel={(opt) => opt.parentName || opt.email || ""}
                    value={user}
                    onChange={(e, newVal) => setUser(newVal || null)}
                    renderInput={(params) => (
                      <TextField {...params} label="User" fullWidth required />
                    )}
                  />
                </div>

                <div className="mb-3">
                  <Autocomplete
                    options={programsList}
                    getOptionLabel={(opt) => opt.name || ""}
                    value={program}
                    onChange={handleProgramSelect}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Program"
                        fullWidth
                        required
                      />
                    )}
                  />
                </div>

                <div className="mb-3">
                  <Autocomplete
                    options={["Full Day", "Half Day"]}
                    value={dayType}
                    onChange={handleDayTypeChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Day Type"
                        fullWidth
                        required
                      />
                    )}
                  />
                </div>

                <TextField
                  label="Amount ($)"
                  value={amount}
                  fullWidth
                  className="mb-3"
                  InputProps={{ readOnly: true }}
                />

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={handleDateChange}
                    slotProps={{
                      textField: { fullWidth: true, required: true },
                    }}
                  />
                </LocalizationProvider>

                <hr className="my-4" />
                <h5 className="mb-3">Optional Add-ons</h5>
                <div className="row g-3">
                  <div className="col-sm-8">
                    <TextField
                      label="Golden Hour Text"
                      value={goldenText}
                      onChange={(e) => setGoldenText(e.target.value)}
                      fullWidth
                    />
                  </div>
                  <div className="col-sm-4">
                    <TextField
                      label="Golden Hour Cost"
                      type="number"
                      value={goldenPrice}
                      onChange={(e) => setGoldenPrice(e.target.value)}
                      fullWidth
                    />
                  </div>
                  <div className="col-sm-8">
                    <TextField
                      label="Enrollment Text"
                      value={enrollmentText}
                      onChange={(e) => setEnrollmentText(e.target.value)}
                      fullWidth
                    />
                  </div>
                  <div className="col-sm-4">
                    <TextField
                      label="Enrollment Cost"
                      type="number"
                      value={enrollmentPrice}
                      onChange={(e) => setEnrollmentPrice(e.target.value)}
                      fullWidth
                    />
                  </div>
                  <div className="col-sm-8">
                    <TextField
                      label="Lunch Text"
                      value={lunchText}
                      onChange={(e) => setLunchText(e.target.value)}
                      fullWidth
                    />
                  </div>
                  <div className="col-sm-4">
                    <TextField
                      label="Lunch Cost"
                      type="number"
                      value={lunchPrice}
                      onChange={(e) => setLunchPrice(e.target.value)}
                      fullWidth
                    />
                  </div>
                </div>
                <div className="text-center mt-4">
                  <button
                    className="btn btn-primary"
                    onClick={handleCreate}
                    disabled={buttonLoading}
                    style={{ color: "#fff", minWidth: 120 }}
                  >
                    {buttonLoading ? (
                      <i className="fa fa-spinner fa-spin" />
                    ) : (
                      "Create"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deletePopup && (
        <div className="popOuter">
          <div className="popInner" style={{ maxWidth: 500, minWidth: 500 }}>
            <i
              className="fas fa-times"
              onClick={() => {
                setDeletePopup(false);
                setSelectedInvoice(null);
              }}
            />
            <div className="popBody">
              <h4>Delete Invoice?</h4>
              <p className="mb-0 fw-bold">
                {selectedInvoice?.userId?.parentName}
              </p>
              <p style={{ fontSize: 15, marginBottom: 15 }}>
                Program: {selectedInvoice?.program}
              </p>
              <div className="text-center">
                <button
                  className="btn btn_danger btn-primary py-2 me-2"
                  disabled={deleteLoading}
                  onClick={handleDelete}
                >
                  {deleteLoading ? (
                    <div className="spinner-border button mx-4" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  ) : (
                    "Yes, Delete"
                  )}
                </button>
                <button
                  className="btn btn-primary py-2"
                  onClick={() => {
                    setDeletePopup(false);
                    setSelectedInvoice(null);
                  }}
                >
                  No, Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
